/**
 * StrategyComparison.tsx
 *
 * Table component comparing metrics across all strategies and benchmarks.
 */

import React from 'react';
import { BacktestResponse, formatStrategyName, getStrategyColor } from '../../types/backtest';

interface StrategyComparisonProps {
  results: BacktestResponse;
}

const StrategyComparison: React.FC<StrategyComparisonProps> = ({ results }) => {
  const formatPercent = (value: number | null): string => {
    if (value === null || value === undefined) return '—';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatCurrency = (value: number): string => {
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value.toFixed(0)}`;
  };

  const formatRatio = (value: number | null): string => {
    if (value === null) return 'N/A';
    return value.toFixed(2);
  };

  const signTone = (value: number): string => (value >= 0 ? 'is-gain' : 'is-loss');
  const drawdownTone = (value: number | null): string => (value == null ? '' : value >= -10 ? 'is-warn' : 'is-loss');

  // Combine strategies and benchmarks for the table
  const strategies = Object.entries(results.strategies);
  const benchmarks = Object.entries(results.benchmarks);

  // Determine best values for highlighting
  const allReturns = [
    ...strategies.map(([_, s]) => s.annualized_return),
    ...benchmarks.map(([_, b]) => b.annualized_return)
  ];
  const bestReturn = Math.max(...allReturns);

  const allDrawdowns = [
    ...strategies.map(([_, s]) => s.max_drawdown),
    ...benchmarks.map(([_, b]) => b.max_drawdown)
  ].filter((v): v is number => v != null);
  const bestDrawdown = Math.max(...allDrawdowns); // Least negative

  return (
    <div>
      <div className="bt-tblw">
        <table className="bt-tbl">
          <thead>
            <tr>
              <th className="l">Strategy</th>
              <th>Total Return</th>
              <th>Annualized</th>
              <th>Max Drawdown</th>
              <th>Sharpe Ratio</th>
              <th>Win Rate</th>
              <th>Profit Factor</th>
              <th>Total Trades</th>
            </tr>
          </thead>
          <tbody>
            {/* Strategies */}
            {strategies.map(([name, data]) => (
              <tr key={name}>
                <td className="l">
                  <span className="bt-name">
                    <span className="bt-dot" style={{ background: getStrategyColor(name) }} />
                    <span>{formatStrategyName(name)}</span>
                    {results.comparison_summary.best_strategy === name && (
                      <span className="bt-tag is-t1">Best</span>
                    )}
                  </span>
                </td>
                <td className={`n ${signTone(data.total_return_pct)}`}>
                  {formatPercent(data.total_return_pct)}
                </td>
                <td className={`n ${signTone(data.annualized_return)}${data.annualized_return === bestReturn ? ' is-strong' : ''}`}>
                  {formatPercent(data.annualized_return)}
                </td>
                <td className={`n ${drawdownTone(data.max_drawdown)}${data.max_drawdown === bestDrawdown ? ' is-strong' : ''}`}>
                  {formatPercent(data.max_drawdown)}
                </td>
                <td className="n">
                  {formatRatio(data.sharpe_ratio)}
                </td>
                <td className="n">
                  {data.win_rate.toFixed(1)}%
                </td>
                <td className="n">
                  {data.profit_factor >= 999 ? '999.9+' : formatRatio(data.profit_factor)}
                </td>
                <td className="n is-t2">
                  {data.total_trades}
                </td>
              </tr>
            ))}

            {/* Separator */}
            <tr className="is-sep" aria-hidden="true">
              <td colSpan={8} />
            </tr>

            {/* Benchmarks */}
            {benchmarks.map(([name, data]) => (
              <tr key={name} className="is-bench">
                <td className="l">
                  <span className="bt-name">
                    <span className="bt-dot" style={{ background: getStrategyColor(name) }} />
                    <span>{name}</span>
                    <span className="bt-tag">Benchmark</span>
                  </span>
                </td>
                <td className={`n ${signTone(data.total_return_pct)}`}>
                  {formatPercent(data.total_return_pct)}
                </td>
                <td className={`n ${signTone(data.annualized_return)}`}>
                  {formatPercent(data.annualized_return)}
                </td>
                <td className={`n ${drawdownTone(data.max_drawdown)}`}>
                  {formatPercent(data.max_drawdown)}
                </td>
                <td className="n is-t3">-</td>
                <td className="n is-t3">-</td>
                <td className="n is-t3">-</td>
                <td className="n is-t3">-</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Metrics Explanation */}
      <p className="bt-guide">
        <strong>Metrics Guide:</strong>{' '}
        <span>Sharpe Ratio</span> measures risk-adjusted returns (higher is better).{' '}
        <span>Profit Factor</span> = total wins / total losses (above 1.5 is good).{' '}
        <span>Max Drawdown</span> = largest peak-to-trough decline (less negative is better).
      </p>
    </div>
  );
};

export default StrategyComparison;
